import { useCallback, useEffect, useMemo } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import { Redirect, useParams } from 'react-router-dom'
import { useMediaQuery } from '@react-hookz/web'
import clsx from 'clsx'
import { observer } from 'mobx-react'

import { ContentSidebar } from 'Components/ContentSidebar/ContentSidebar'
import { FaqCard } from 'Components/FaqCard/FaqCard'
import { NetPriceCalculator } from 'Components/NetPriceCalculator/NetPriceCalculator'
import { PageErrorState } from 'Components/PageErrorState/PageErrorState'
import { PageLoadingState } from 'Components/PageLoadingState/PageLoadingState'
import { PreviewColumnTypeEnum } from 'Components/PreviewColumn/PreviewColumn.types'

import { useStore } from 'Stores/index'
import { OrderStepEnum } from 'Stores/orderStore/orderStore.types'

import { useActiveUser, useIsOrganisation } from 'Hooks'

import { PORTAL_BREAKPOINTS } from 'Constants/constants'
import { E2E_PREVIEW_COLUMN_ORDER_FOOTER_BUTTON } from 'Constants/e2e'

import { OrderBundlePreviewColumn } from '../OrderBundlePreviewColumn/OrderBundlePreviewColumn'
import { OrderGuardModal } from '../OrderGuardModal/OrderGuardModal'
import { OrderWrapperProps } from './OrderWrapper.types'

export const OrderWrapper = observer((props: OrderWrapperProps) => {
  const { employeeId, orderFlowType, renderOrderContent } = props

  const { orderStore, portalConfigStore, portalSettingsStore, rentalStore } =
    useStore()
  const {
    currentStep,
    fetchCatalog,
    isLoading,
    setChosenDevice,
    clearChosenDeviceLimitationRules,
    previewColumnDevice,
    isOrderDisabled,
    orderDevices,
  } = orderStore
  const { portalConfig } = portalConfigStore
  const { setTogglePreviewColumn, previewColumnSettings, isAppReadOnly } =
    portalSettingsStore
  const { rentalDetails } = rentalStore
  const { activeEmployee, activeOrganisation } = useActiveUser()
  const isOrganisation = useIsOrganisation()

  const {
    employeeId: employeeIdFromParams,
    organisationId: organisationIdFromParams,
  } = useParams<{
    employeeId: string
    organisationId: string
  }>()

  const isTablet = useMediaQuery(`(min-width: ${PORTAL_BREAKPOINTS.LG})`)
  const isExtraLarge = useMediaQuery(`(min-width: ${PORTAL_BREAKPOINTS.XXL})`)

  const previewColumnSetting = previewColumnSettings.find(
    previewColumnSetting =>
      previewColumnSetting.type === PreviewColumnTypeEnum.Bundle,
  )

  const isBundlePreviewColumnSetting =
    previewColumnSetting &&
    previewColumnSetting.type === PreviewColumnTypeEnum.Bundle

  const isNetPriceCalculatorVisible = useMemo(() => {
    const groupDevicesByRentCategory =
      !!portalConfig?.groupDevicesByRentCategory

    if (
      !groupDevicesByRentCategory &&
      currentStep !== OrderStepEnum.result &&
      (activeEmployee?.organisation?.isPlusOrganisation ||
        activeOrganisation?.isPlusOrganisation)
    ) {
      return true
    }

    return false
  }, [currentStep, activeEmployee, activeOrganisation, portalConfig])

  const renderSideContent = useMemo(
    () => (
      <>
        {isNetPriceCalculatorVisible && <NetPriceCalculator />}

        <FaqCard isOnOrderFlow />
      </>
    ),
    [isNetPriceCalculatorVisible],
  )

  const onClosePreviewColumn = useCallback(() => {
    clearChosenDeviceLimitationRules()
    setTogglePreviewColumn(PreviewColumnTypeEnum.Bundle, false)
  }, [clearChosenDeviceLimitationRules, setTogglePreviewColumn])

  useEffect(() => {
    if (employeeIdFromParams && !organisationIdFromParams && !activeEmployee)
      return

    fetchCatalog(orderFlowType, employeeId, isOrganisation)
  }, [
    activeEmployee,
    employeeId,
    employeeIdFromParams,
    fetchCatalog,
    isOrganisation,
    orderFlowType,
    organisationIdFromParams,
  ])

  // Check conditions block only when backend returns some devices to choose from
  if (
    (orderDevices?.allDevices && orderDevices?.allDevices.length > 0) ||
    (orderDevices?.groupedDevices && orderDevices?.groupedDevices?.length > 0)
  ) {
    // CONDITIONS BLOCK TO EXAMINE IF USER TIRES TO MANIPULATE URL AND ITS IDs
    if (orderFlowType === 'NEW_ORDER') {
      // logged user portal config disallows new order
      if (!portalConfig?.newDevice) {
        return <Redirect to="/" />
      }

      // Organisation cant order new device
      // Do not trigger when organisation performs "Rent new device" action for specific employee
      if (
        isOrganisation &&
        !employeeId &&
        !portalConfig?.showNewDeviceButtonToAdmin
      ) {
        return <Redirect to="/" />
      }
    }

    // performs early upgrade on device when the action is disabled
    if (
      orderFlowType === 'EARLY_UPGRADE' &&
      !rentalDetails?.rental.actions.showEarlyUpgradeAction
    ) {
      return <Redirect to="/" />
    }

    // performs prolong on device when the action is disabled
    if (
      orderFlowType === 'PROLONG' &&
      !rentalDetails?.rental.actions.showDeviceProlongingAction
    ) {
      return <Redirect to="/" />
    }

    // performs upgrade on device when the action is disabled
    if (
      orderFlowType === 'UPGRADE' &&
      !rentalDetails?.rental.actions.showStandardUpgradeAction
    ) {
      return <Redirect to="/" />
    }

    // User tries to prolong/early/upgrade device from different employee than the device owner
    if (
      orderFlowType !== 'NEW_ORDER' &&
      activeEmployee &&
      activeEmployee.id !== rentalDetails?.rental.employee?.id
    ) {
      return <Redirect to="/" />
    }
    // END OF BLOCK
  }

  if (isOrderDisabled) {
    return <Redirect to="/" />
  }

  if (isAppReadOnly) {
    return <PageErrorState />
  }

  return (
    <Container className="OrderFlow pt-6" fluid>
      <Row
        className={clsx(
          isExtraLarge &&
            currentStep === OrderStepEnum.device &&
            'fill-content-height',
        )}
      >
        <Col xl={12} xs={{ order: 'last' }} xxl={{ order: 'first', span: 8 }}>
          {isLoading ? <PageLoadingState /> : renderOrderContent()}
        </Col>

        {isBundlePreviewColumnSetting ? (
          <ContentSidebar
            isPreviewColumnShown={previewColumnSetting.isOpen}
            renderStickyColumnSideContent={renderSideContent}
          >
            {previewColumnDevice?.isBundle && isTablet && (
              <OrderBundlePreviewColumn
                basisAmount={previewColumnDevice.bundleBasisAmount}
                copaymentGross={previewColumnDevice.bundleCopaymentGross}
                copaymentNet={previewColumnDevice.bundleCopaymentNet ?? 0}
                device={previewColumnDevice}
                e2eSelectorFooterBtn={E2E_PREVIEW_COLUMN_ORDER_FOOTER_BUTTON}
                isOpen={previewColumnSetting.isOpen}
                onClosePreviewColumn={onClosePreviewColumn}
                onRentBundleButtonAction={async () => {
                  await setChosenDevice(previewColumnDevice)
                  previewColumnSetting.handleOnRentAction()
                  setTogglePreviewColumn(PreviewColumnTypeEnum.Bundle, false)
                }}
              />
            )}
          </ContentSidebar>
        ) : (
          <Col xl={12} xs={{ order: 'first' }} xxl={{ order: 'last', span: 4 }}>
            {renderSideContent}
          </Col>
        )}
      </Row>

      <OrderGuardModal />
    </Container>
  )
})
